/** Time axis of the wind forecast: slider range, default hour and Europe/Zurich labels. */

export const ZURICH = "Europe/Zurich";
export const HOUR_MS = 3_600_000;
/** The slider moves in half hours; the layer interpolates between the hourly fields. */
export const SLIDER_STEP_MS = 30 * 60_000;

export interface TimeRange {
  min: number;
  max: number;
}

export function timeRange(times: readonly string[]): TimeRange {
  return { min: Date.parse(times[0]!), max: Date.parse(times[times.length - 1]!) };
}

/** The current hour (rounded down), clamped to the forecast. */
export function defaultTime(range: TimeRange, nowMs: number): number {
  const hour = Math.floor(nowMs / HOUR_MS) * HOUR_MS;
  return Math.min(range.max, Math.max(range.min, hour));
}

export const clampTime = (range: TimeRange, t: number): number =>
  Math.min(range.max, Math.max(range.min, t));

export const dayKey = (ms: number): string =>
  new Intl.DateTimeFormat("en-CA", { timeZone: ZURICH }).format(new Date(ms));

/**
 * "15:00" later the same Zurich day, "Do 06:00" on another day ("now" itself is labelled by the
 * caller, only for the default position). Always Zurich time, whatever the device zone (the
 * lakes are there; DST changes included).
 */
export function formatWindTime(ms: number, nowMs: number, locale: string): string {
  const time = new Intl.DateTimeFormat(locale, {
    timeZone: ZURICH,
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  }).format(new Date(ms));
  if (dayKey(ms) === dayKey(nowMs)) return time;
  const day = new Intl.DateTimeFormat(locale, { timeZone: ZURICH, weekday: "short" })
    .format(new Date(ms))
    .replace(/\.$/, "");
  return `${day} ${time}`;
}

/** Clock time of the model run in Zurich time, e.g. "14:00". */
export function formatRunTime(iso: string, locale: string): string {
  return new Intl.DateTimeFormat(locale, {
    timeZone: ZURICH,
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  }).format(new Date(iso));
}
